import React, { useEffect, useRef } from "react"
import { bugsnagClient } from "../shared/bugsnagClient"
import { classifyMediaError } from "./deviceCheckRules"

/**
 * L'aperçu de la caméra pendant le test d'entrée, sur le web.
 *
 * `CameraView` d'expo-camera ne sait pas ouvrir un objectif désigné : sa
 * version web ne connaît que `facingMode`. Le sélecteur du parcours changeait
 * donc l'appareil **sondé** sans changer l'appareil **affiché** — l'écran
 * disait « essaie l'autre caméra », l'élève cliquait, et l'image ne bougeait
 * pas. Impossible pour lui de savoir s'il avait trouvé la bonne.
 *
 * On ouvre donc le flux nous-mêmes, sur l'objectif choisi. Trois points de
 * vigilance :
 *
 * - **Contrainte stricte ici, et c'est volontaire.** À l'entrée en séance on
 *   demande le périphérique en « souhaité », pour ne jamais bloquer une
 *   arrivée en cours. Ici c'est l'inverse : l'élève est devant l'écran, en
 *   train de choisir, et un repli silencieux sur une autre caméra lui ferait
 *   croire que son clic n'a servi à rien. Mieux vaut un échec dit qu'une
 *   image trompeuse — les caméras virtuelles (OBS et consorts) sont
 *   précisément le cas : listées, mais souvent impossibles à ouvrir.
 * - **L'échec se voit.** Il remonte à l'appelant au lieu d'être avalé. La
 *   première version gardait l'ancien flux à l'écran sans un mot : on cliquait
 *   sur une autre caméra et il ne se passait rien du tout.
 * - **Le flux est rendu à la sortie.** C'est LiveKit qui prend la suite juste
 *   après ; une piste laissée ouverte ici lui refuserait l'objectif.
 *
 * L'analyse d'obstruction lit ce `<video>` — c'est déjà ce qu'elle cherche
 * dans le conteneur de l'aperçu, elle n'a pas à savoir qui l'a monté.
 */
const CameraPreview = ({ deviceId, onDeviceError }) => {
    const videoRef = useRef(null)
    const streamRef = useRef(null)
    // Par référence : une fonction recréée à chaque rendu rouvrirait la caméra
    // à chaque rendu.
    const onErrorRef = useRef(onDeviceError)
    onErrorRef.current = onDeviceError

    /*
     * Le flux n'est rendu qu'au démontage, jamais au changement d'objectif.
     *
     * En le relâchant dans le nettoyage de l'effet, React coupait l'image
     * AVANT d'essayer la nouvelle caméra : quand celle-ci ne répondait pas,
     * l'aperçu tombait au noir — alors que la pastille affirmait « tu vois
     * toujours la précédente ». L'écran mentait exactement au moment où
     * l'élève avait besoin qu'on lui dise la vérité.
     */
    useEffect(
        () => () => {
            if (videoRef.current) videoRef.current.srcObject = null
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop())
                streamRef.current = null
            }
        },
        []
    )

    useEffect(() => {
        let cancelled = false

        /** L'objectif précédent, rendu seulement une fois le nouveau obtenu. */
        const releasePrevious = (previous) => {
            if (!previous) return
            previous.getTracks().forEach((track) => track.stop())
        }

        const open = async () => {
            if (typeof navigator === "undefined" || !navigator.mediaDevices) return
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    // `exact` : voir l'en-tête. Sans lui, une caméra
                    // indisponible se solderait par un repli muet sur une
                    // autre, et le choix de l'élève disparaîtrait en silence.
                    video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "user" },
                })
                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop())
                    return
                }

                const previous = streamRef.current
                streamRef.current = stream
                if (videoRef.current) {
                    videoRef.current.srcObject = stream
                    // La lecture refusée (l'autoplay du navigateur) laisse l'aperçu figé, sans
                    // plus : un fil d'Ariane, pas une erreur.
                    videoRef.current.play().catch((cause) => {
                        bugsnagClient.leaveBreadcrumb("precall: preview play refused", {
                            reason: cause?.name ?? "unknown",
                        })
                    })
                }
                // Une fois le nouveau à l'écran, et pas avant.
                releasePrevious(previous)
                onErrorRef.current?.(null)
            } catch (error) {
                // L'ancien flux reste à l'écran — on ne prive pas l'élève
                // d'image — mais l'étape doit dire que cette caméra-là n'a pas
                // répondu, sinon le clic paraît sans effet.
                // La cause, pas le nom brut de l'erreur : le dossier a déjà son
                // classifieur et ses phrases par cause. Remonter `error.name`
                // aurait introduit un second vocabulaire, et privé l'élève de
                // la précision que l'écran sait produire ailleurs.
                if (!cancelled) onErrorRef.current?.(classifyMediaError(error))
            }
        }

        open()
        return () => {
            cancelled = true
        }
    }, [deviceId])

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                // Comme expo-camera sur une caméra frontale : sans miroir,
                // l'élève se voit à l'envers et croit que quelque chose cloche.
                transform: "scaleX(-1)",
            }}
        />
    )
}

export default CameraPreview
